import React from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import numeral from 'numeral'
import { useSelector } from 'react-redux'
import moment from 'moment'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'

import { TotalPosIcon, ArrowIcon, SalesTargetIcon, DollarOrderIcon, CashierAmountIcon, BillsIcon, WalletIcon, MaterialCostIcon, PercentageIcon, LaborCostIcon, TrendsIcon } from '../icons'

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const getDuration = (minutes) => {
  const duartion = moment.duration(minutes, 'minutes')
  const hours = Math.floor(duartion.asHours())
  const mins = duartion.minutes()
  return `${hours}h ${mins}m`
}

const DiffRow = ({current, previous, label = '', currency}) => {
  const { t } = useTranslation()

  const value = (() => {
    if (previous === 0) {
      return current === 0 ? 0 : 100
    }
    return (((current - previous) / previous) * 100)
  })()

  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  const formatted = `${sign}${numeral(Math.abs(value)).format('0,0.00')}%`

  return (
    <div style={{display: 'flex', alignItems: 'center', color: value > 0 ? '#00B74A' : value < 0 ? '#FF5630' : '#808F94'}}>
      {
        sign !== ''
          ? <ArrowIcon style={{transform: `rotate(${sign === '-' ? 90 : 0}deg)`}} />
          : null
      }
      <span className={cn({[styles.positive]: sign === '+', [styles.negative]: sign === '-'})}>{formatted} {label}</span>&nbsp;{!label ? <span style={{color: '#808F94'}}>{t('vs previous')}</span> : null}
    </div>
  )
}

const SkeletonCard = () => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <span className={styles.skeleton} style={{ width: '60%', height: 12 }} />
      <span className={styles.skeleton} style={{ width: 20, height: 20 }} />
    </div>
    <span className={styles.skeleton} style={{ width: '80%', height: 28, marginTop: 4 }} />
    <span className={styles.skeleton} style={{ width: '70%', height: 12, marginTop: 4 }} />
  </div>
)

const SummaryCards = ({data, loading}) => {
  const { t } = useTranslation()
  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const { current, previous } = data || {}

  if (loading) {
    return (
      <div className={styles.container}>
        {Array.from({length: 13}, (_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={cn(styles.card, {[styles.negative]: current.salesAfterDiscount < previous.salesAfterDiscount, [styles.positive]: current.salesAfterDiscount > previous.salesAfterDiscount})}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Total POS sales')}</div>
          <TotalPosIcon />
        </div>
        <div className={styles.cardValue}>
          {formatNumber(current.salesAfterDiscount, currency.symbol)}
        </div>
        <div className={styles.cardRow}>
          <DiffRow
            current={current.salesAfterDiscount}
            previous={previous.salesAfterDiscount} />
        </div>
      </div>
      <div className={cn(styles.card, {[styles.negative]: current.salesTarget > current.salesAfterDiscount, [styles.positive]: current.salesTarget < current.salesAfterDiscount})}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Sales target')}</div>
          <SalesTargetIcon />
        </div>
        <div className={styles.cardValue}>
          {`${numeral(current.salesAfterDiscount).format('0,0')} ${currency.symbol}`} / {`${numeral(current.salesTarget).format('0,0')} ${currency.symbol}`}
        </div>
        <div className={styles.cardRow}>
          <DiffRow
            current={current.salesAfterDiscount}
            previous={current.salesTarget}
            label={t('achieved')} />
        </div>
      </div>
      <div className={cn(styles.card, {[styles.negative]: current.netSalesWithoutVat < previous.netSalesWithoutVat, [styles.positive]: current.netSalesWithoutVat > previous.netSalesWithoutVat})}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Sales excluding VAT')}</div>
          <DollarOrderIcon />
        </div>
        <div className={styles.cardValue}>
          {formatNumber(current.netSalesWithoutVat, currency.symbol)}
        </div>
        <div className={styles.cardRow}>
          <DiffRow
            current={current.netSalesWithoutVat}
            previous={previous.netSalesWithoutVat} />
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Cashier shift amount')}</div>
          <CashierAmountIcon />
        </div>
        <div className={styles.cardValue}>
          {formatNumber(current.zShiftTotal, currency.symbol)}
        </div>
        {/* <div className={styles.cardRow}>
          <DiffRow
            current={current.netSalesWithoutVat}
            previous={previous.netSalesWithoutVat} />
        </div> */}
      </div>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Number of checks')}</div>
          <BillsIcon />
        </div>
        <div className={styles.cardValue}>
          {current.bills}
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Average check')}</div>
          <WalletIcon />
        </div>
        <div className={styles.cardValue}>
          {formatNumber(current.averageCheck, currency.symbol)}
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Raw material cost')}</div>
          <MaterialCostIcon />
        </div>
        <div className={styles.cardValue}>
          {formatNumber(current.cost, currency.symbol)}
        </div>
        <div className={styles.cardRow}>
          {t('Food cost')} {numeral(current.foodCostPct).format('0.[00]')}%
        </div>
      </div>
      <div className={cn(styles.card, {[styles.negative]: current.foodCostPct > current.materialCostPercentage, [styles.positive]: current.foodCostPct < current.materialCostPercentage})}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Raw material cost %')}</div>
          <PercentageIcon />
        </div>
        <div className={styles.cardValue}>
          {numeral(current.foodCostPct).format('0.[00]')}%
        </div>
        <div className={styles.cardRow}>
          {t('Target')} {numeral(current.materialCostPercentage).format('0.[00]')}%
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Labor cost')}</div>
          <LaborCostIcon />
        </div>
        <div className={styles.cardValue}>
          {formatNumber(current.employeeCost, currency.symbol)}
        </div>
        <div className={styles.cardRow}>
          { getDuration(current.employeeDuration) }
        </div>
      </div>
      <div className={cn(styles.card, {[styles.negative]: current.employeeCostPct > current.employeeCostPercentage, [styles.positive]: current.employeeCostPct < current.employeeCostPercentage})}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Labor cost %')}</div>
          <PercentageIcon />
        </div>
        <div className={styles.cardValue}>
          {numeral(current.employeeCostPct).format('0.[00]')}%
        </div>
        <div className={styles.cardRow}>
          {t('Target')} {numeral(current.employeeCostPercentage).format('0.[00]')}%
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Gross profit')}</div>
          <TrendsIcon />
        </div>
        <div className={styles.cardValue}>
          {formatNumber(current.grossProfit, currency.symbol)}
        </div>
        <div className={cn(styles.cardRow, {[styles.positive]: current.salesAfterDiscount - current.cost > 0, [styles.negative]: current.salesAfterDiscount - current.cost < 0})}>
          {t('preliminary')}
        </div>
      </div>
      <div className={cn(styles.card, {[styles.positive]: current.grossMarging > 0, [styles.negative]: current.grossMarging < 0})}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Gross margin %')}</div>
          <PercentageIcon />
        </div>
        <div className={styles.cardValue}>
          {numeral(current.grossMarging).format('0.[00]')}%
        </div>
        <div className={cn(styles.cardRow, {[styles.positive]: current.grossMarging > 0, [styles.negative]: current.grossMarging < 0})}>
          {t('raw materials + labor')}
        </div>
      </div>
      <div className={cn(styles.card, styles.fullWidth)}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('Difference from POS')}</div>
        </div>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div className={styles.cardRow}>
            <div className={styles.cardValue}>
              { formatNumber(current.zShiftTotal - current.salesAfterDiscount, currency.symbol) }
            </div>
            {
              (() => {
                const posSales = current.salesAfterDiscount
                const diff = numeral(numeral(current.zShiftTotal - posSales).format('0.[000]')).value()
                const diffPct = current.zShiftTotal > 0 ? (diff / posSales) * 100 : null
                const status = diffPct == null ? "—" : Math.abs(diffPct) <= 1 ? 'OK' : 'Check'

                return (
                  <>
                    <span style={{fontSize: 14}}>{diffPct !== null ? `(${numeral(diffPct).format('0.[00]')}%)` : null}</span>
                    <div className={cn(styles.badge, {[styles.positive]: status === 'OK', [styles.negative]: status === 'Check'})}>
                      { t(status) }
                    </div>
                  </>
                )
              })()
            }
          </div>
          <div className={styles.cardRow}>
            {t('Shifts')}: CLOSED <b>{current.statuses?.CLOSED.count}</b> · UNACCEPTED <b>{current.statuses?.UNACCEPTED.count}</b> · OPEN ({t('not included')}) <b>{current.statuses?.OPEN.count}</b>
          </div>
        </div>
        
      </div>
    </div>
  )
}

export default SummaryCards
